// SapLockTracePanel — modal "Lock Trace" di SAP User Maintenance.
// Sumber: Security Audit Log (SM20) via RSAU_READ_FILE (backend/sapLockTrace.js).
// Tiga bagian: Trace (live per user), Recent locks (hasil poller), Alert settings.
// Teks literal Inggris (bukan i18n) — mengikuti konvensi panel analitik lain.
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../context/AuthContext';
import {
  X, Search, RefreshCw, Lock, AlertCircle, CheckCircle2, Bell, Monitor, History, Send,
} from 'lucide-react';

const today = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const EV_STYLE = {
  AUM: 'bg-rose-600 text-white border-rose-600',
  AU2: 'bg-rose-50 text-rose-700 border-rose-200',
  AU1: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  AUC: 'bg-slate-50 text-slate-500 border-slate-200',
};

function Owner({ owner, ip }) {
  if (!owner) return <span className="text-slate-300">—</span>;
  return <span className="text-slate-600">{owner}{ip ? <span className="text-slate-400"> · {ip}</span> : null}</span>;
}

export default function SapLockTracePanel({ connId, profileLabel, initialUser = '', onClose }) {
  const [tab, setTab] = useState(initialUser ? 'trace' : 'recent');
  const [user, setUser] = useState(initialUser);
  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  const [hideOk, setHideOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  const [recent, setRecent] = useState(null);
  const [days, setDays] = useState(7);
  const [cfg, setCfg] = useState(null);
  const [cfgMsg, setCfgMsg] = useState(null);

  const apiErr = (e) => e?.response?.data?.error || e.message || 'Error';

  const runTrace = useCallback(async (u) => {
    const uname = String(u ?? user).trim().toUpperCase();
    if (!uname || !connId) return;
    setBusy(true); setErr(''); setResult(null);
    try {
      const { data } = await api.get('/api/sap/lock-trace', { params: { connectionId: connId, user: uname, dateFrom, dateTo } });
      setResult(data);
    } catch (e) { setErr(apiErr(e)); }
    finally { setBusy(false); }
  }, [user, connId, dateFrom, dateTo]);

  const loadRecent = useCallback(async () => {
    try {
      const { data } = await api.get('/api/sap/lock-trace/recent', { params: { connectionId: connId, days } });
      setRecent(data.rows || []);
    } catch (e) { setErr(apiErr(e)); setRecent([]); }
  }, [connId, days]);

  const loadCfg = useCallback(async () => {
    try { const { data } = await api.get('/api/sap/lock-trace/config'); setCfg(data); }
    catch (e) { setCfgMsg({ type: 'error', text: apiErr(e) }); }
  }, []);

  useEffect(() => { if (initialUser) runTrace(initialUser); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (tab === 'recent') loadRecent(); }, [tab, loadRecent]);
  useEffect(() => { if (tab === 'alert') loadCfg(); }, [tab, loadCfg]);

  const saveCfg = async (patch) => {
    setCfgMsg(null);
    try {
      const { data } = await api.put('/api/sap/lock-trace/config', { ...patch });
      setCfg(c => ({ ...c, ...data }));
      setCfgMsg({ type: 'success', text: 'Saved.' });
    } catch (e) { setCfgMsg({ type: 'error', text: apiErr(e) }); }
  };
  const pollNow = async () => {
    setCfgMsg(null);
    try {
      const { data } = await api.post('/api/sap/lock-trace/poll');
      setCfgMsg({ type: data.error ? 'error' : 'success', text: data.error || data.detail || 'Done.' });
      loadCfg();
    } catch (e) { setCfgMsg({ type: 'error', text: apiErr(e) }); }
  };
  const testPush = async () => {
    setCfgMsg(null);
    try {
      const { data } = await api.post('/api/sap/lock-trace/test-push');
      setCfgMsg({ type: data.sent ? 'success' : 'error', text: data.sent ? `Test push sent to ${data.sent} device(s).` : `Not sent: ${data.error || 'no devices'}` });
    } catch (e) { setCfgMsg({ type: 'error', text: apiErr(e) }); }
  };

  const traceUser = (u) => { setUser(u); setTab('trace'); runTrace(u); };
  const events = (result?.events || []).filter(e => !hideOk || e.ev === 'AU2' || e.ev === 'AUM');

  const TabBtn = ({ k, Icon, label }) => (
    <button onClick={() => setTab(k)}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all
        ${tab === k ? 'bg-white text-slate-800 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:text-slate-700'}`}>
      <Icon style={{ width: 14, height: 14 }} /> {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-2 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100">
          <div className="min-w-0">
            <h3 className="font-display font-bold text-slate-800 text-base flex items-center gap-2">
              <Lock className="text-rose-500" style={{ width: 18, height: 18 }} /> Lock Trace
            </h3>
            <p className="text-xs text-slate-500 mt-0.5 truncate">
              Failed logons &amp; user locks from the Security Audit Log (SM20){profileLabel ? ` — ${profileLabel}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X style={{ width: 20, height: 20 }} /></button>
        </div>

        <div className="px-5 pt-3">
          <div className="inline-flex gap-1 bg-slate-100/80 rounded-2xl p-1">
            <TabBtn k="trace" Icon={Search} label="Trace user" />
            <TabBtn k="recent" Icon={History} label="Recent locks" />
            <TabBtn k="alert" Icon={Bell} label="Alert" />
          </div>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4">
          {err && (
            <div className="mb-3 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              <AlertCircle style={{ width: 15, height: 15 }} className="flex-shrink-0 mt-0.5" /> <span className="flex-1">{err}</span>
              <button onClick={() => setErr('')}><X style={{ width: 14, height: 14 }} /></button>
            </div>
          )}

          {/* ── TRACE ── */}
          {tab === 'trace' && (
            <>
              <form onSubmit={(e) => { e.preventDefault(); runTrace(); }} className="flex flex-wrap items-end gap-2 mb-4">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1">SAP user</label>
                  <input value={user} onChange={(e) => setUser(e.target.value.toUpperCase())} placeholder="e.g. PGC_SUPPORT"
                    className="w-48 px-3 py-1.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-blue-300" />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1">From</label>
                  <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                    className="px-3 py-1.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-blue-300" />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1">To</label>
                  <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                    className="px-3 py-1.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-blue-300" />
                </div>
                <button type="submit" disabled={busy || !user.trim()}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">
                  <RefreshCw style={{ width: 14, height: 14 }} className={busy ? 'animate-spin' : ''} /> {busy ? 'Reading SM20…' : 'Trace'}
                </button>
                <span className="text-[11px] text-slate-400">Max 31 days · read live from SAP</span>
              </form>

              {result && (
                <>
                  {/* Ringkasan */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    <div className={`rounded-xl border px-4 py-3 ${result.summary.locks.length ? 'border-rose-200 bg-rose-50' : 'border-emerald-200 bg-emerald-50'}`}>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Locked</p>
                      {result.summary.locks.length ? result.summary.locks.map((l, i) => (
                        <div key={i} className="mt-1 text-sm">
                          <p className="font-bold text-rose-700">{l.time}</p>
                          <p className="text-xs text-slate-600 flex items-center gap-1"><Monitor style={{ width: 12, height: 12 }} /> {l.terminal || '—'}</p>
                          <p className="text-xs"><Owner owner={l.owner} ip={l.ownerIp} /></p>
                        </div>
                      )) : <p className="mt-1 text-sm font-semibold text-emerald-700">No lock in this period</p>}
                    </div>
                    <div className="rounded-xl border border-slate-200 px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Wrong password attempts</p>
                      <p className="text-2xl font-bold text-slate-800 mt-1">{result.summary.wrongPassword}</p>
                      <p className="text-xs text-slate-500">{result.summary.blockedWhileLocked} more attempt(s) rejected while locked</p>
                    </div>
                    <div className="rounded-xl border border-slate-200 px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Wrong passwords by terminal</p>
                      {result.summary.failedByTerminal.length ? (
                        <ul className="mt-1 space-y-1">
                          {result.summary.failedByTerminal.map(t => (
                            <li key={t.terminal} className="text-xs flex items-center justify-between gap-2">
                              <span className="truncate"><span className="font-semibold text-slate-700">{t.terminal}</span> <Owner owner={t.owner} /></span>
                              <span className="font-bold text-rose-600 whitespace-nowrap">{t.count}×</span>
                            </li>
                          ))}
                        </ul>
                      ) : <p className="mt-1 text-xs text-slate-400">None</p>}
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-600">Timeline — {result.user} · {result.sid}/{result.client} · {events.length} event(s)</p>
                    <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer">
                      <input type="checkbox" checked={hideOk} onChange={(e) => setHideOk(e.target.checked)} /> Failures only
                    </label>
                  </div>
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-500">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold">Time</th>
                          <th className="text-left px-3 py-2 font-semibold">Event</th>
                          <th className="text-left px-3 py-2 font-semibold">Detail</th>
                          <th className="text-left px-3 py-2 font-semibold">Terminal</th>
                          <th className="text-left px-3 py-2 font-semibold">PC owner (SDP)</th>
                          <th className="text-left px-3 py-2 font-semibold">T-code</th>
                        </tr>
                      </thead>
                      <tbody>
                        {events.map((e, i) => (
                          <tr key={i} className={`border-t border-slate-100 ${e.ev === 'AUM' ? 'bg-rose-50/60' : ''}`}>
                            <td className="px-3 py-1.5 whitespace-nowrap font-mono text-slate-600">{e.time}</td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-semibold ${EV_STYLE[e.ev] || 'bg-slate-50 text-slate-500 border-slate-200'}`}>{e.label}</span>
                            </td>
                            <td className="px-3 py-1.5 text-slate-600">{[e.reasonText, e.ltypeText].filter(Boolean).join(' · ') || <span className="text-slate-300">—</span>}</td>
                            <td className="px-3 py-1.5 font-semibold text-slate-700 whitespace-nowrap">{e.terminal || <span className="text-slate-300">—</span>}</td>
                            <td className="px-3 py-1.5 whitespace-nowrap"><Owner owner={e.owner} ip={e.ownerIp} /></td>
                            <td className="px-3 py-1.5 text-slate-500">{e.tcode || <span className="text-slate-300">—</span>}</td>
                          </tr>
                        ))}
                        {!events.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">No events.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-2 text-[11px] text-slate-400">
                    PC owner = last Windows user recorded in the SDP inventory, not necessarily the person at the PC at that moment. Batch logons are hidden.
                  </p>
                </>
              )}
            </>
          )}

          {/* ── RECENT ── */}
          {tab === 'recent' && (
            <>
              <div className="flex items-center gap-2 mb-3">
                <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                  className="px-2 py-1 rounded-lg border border-slate-200 text-xs bg-white outline-none">
                  {[1, 7, 30, 90].map(d => <option key={d} value={d}>Last {d} day{d > 1 ? 's' : ''}</option>)}
                </select>
                <button onClick={loadRecent} className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs border border-slate-200 hover:bg-slate-50">
                  <RefreshCw style={{ width: 12, height: 12 }} /> Refresh
                </button>
                <span className="text-[11px] text-slate-400">Collected by the alert job every 5 minutes</span>
              </div>
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Locked at</th>
                      <th className="text-left px-3 py-2 font-semibold">User</th>
                      <th className="text-left px-3 py-2 font-semibold">Terminal</th>
                      <th className="text-left px-3 py-2 font-semibold">PC owner (SDP)</th>
                      <th className="text-left px-3 py-2 font-semibold">Wrong passwords (6h before)</th>
                      <th className="text-left px-3 py-2 font-semibold">Push</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(recent || []).map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-3 py-1.5 whitespace-nowrap font-mono text-slate-600">{r.time}</td>
                        <td className="px-3 py-1.5 font-semibold text-slate-800">{r.uname}</td>
                        <td className="px-3 py-1.5 whitespace-nowrap">{r.terminal || '—'}</td>
                        <td className="px-3 py-1.5 whitespace-nowrap"><Owner owner={r.owner} ip={r.ownerIp} /></td>
                        <td className="px-3 py-1.5 text-slate-600">{(r.failedBefore || []).map(f => `${f.n}× ${f.terminal}`).join(', ') || '—'}</td>
                        <td className="px-3 py-1.5">{r.notified ? <CheckCircle2 className="text-emerald-500" style={{ width: 14, height: 14 }} /> : <span className="text-slate-300">—</span>}</td>
                        <td className="px-3 py-1.5 text-right">
                          <button onClick={() => traceUser(r.uname)} className="px-2 py-0.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100">Trace</button>
                        </td>
                      </tr>
                    ))}
                    {recent && !recent.length && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">No locks recorded in this period.</td></tr>}
                    {!recent && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">Loading…</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ── ALERT ── */}
          {tab === 'alert' && cfg && (
            <div className="max-w-2xl space-y-4">
              <div className="rounded-xl border border-slate-200 px-4 py-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Push alert when a user gets locked</p>
                  <p className="text-xs text-slate-500">Checks SM20 every 5 minutes. Each new lock is pushed with user, time, terminal, PC owner and the wrong-password attempts before it.</p>
                </div>
                <button onClick={() => saveCfg({ alert_enabled: !cfg.alert_enabled })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${cfg.alert_enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${cfg.alert_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <AlertField label="Push to mobile app(s)" hint="App key(s), comma separated. sapbasisadm = SAP Basis Admin."
                value={cfg.push_apps || ''} onSave={(v) => saveCfg({ push_apps: v })} />
              <AlertField label="Excluded users" hint="No alert for these users (comma separated), e.g. old accounts that stay locked."
                value={cfg.excluded_users || ''} onSave={(v) => saveCfg({ excluded_users: v })} upper />
              <div className="rounded-xl border border-slate-200 px-4 py-3 text-xs text-slate-600 space-y-1">
                <p><span className="font-semibold">Last check:</span> {cfg.last_poll_at || '—'} ({cfg.last_poll_status || 'never'})</p>
                <p className="text-slate-500 break-all">{cfg.last_poll_detail || ''}</p>
                <div className="flex gap-2 pt-2">
                  <button onClick={pollNow} className="flex items-center gap-1 px-3 py-1 rounded-lg border border-slate-200 hover:bg-slate-50 font-semibold">
                    <RefreshCw style={{ width: 12, height: 12 }} /> Check now
                  </button>
                  <button onClick={testPush} className="flex items-center gap-1 px-3 py-1 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 font-semibold">
                    <Send style={{ width: 12, height: 12 }} /> Send test push
                  </button>
                </div>
              </div>
              {cfgMsg && (
                <p className={`text-xs ${cfgMsg.type === 'error' ? 'text-rose-600' : 'text-emerald-600'}`}>{cfgMsg.text}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AlertField({ label, hint, value, onSave, upper }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
      <div className="flex gap-2">
        <input value={v} onChange={(e) => setV(upper ? e.target.value.toUpperCase() : e.target.value)}
          className="flex-1 px-3 py-1.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-blue-300" />
        <button onClick={() => onSave(v)} disabled={v === value}
          className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-slate-800 text-white disabled:opacity-30">Save</button>
      </div>
      <p className="text-[11px] text-slate-400 mt-1">{hint}</p>
    </div>
  );
}
